"use client";
import { Heading } from "@/components/base/heading";
import { LocationButton } from "@/components/center/location-button";
import { ContentContainer } from "@/components/layout/content-container";
import { FullWidthContainer } from "@/components/layout/full-width-container";
import { HorizontalContainer } from "@/components/layout/horizontal-container";
import { HorizontalSpacer } from "@/components/layout/horizontal-spacer";
import { VerticalContainer } from "@/components/layout/vertical-container";
import { VerticalSpacer } from "@/components/layout/vertical-spacer";
import { DynamicZone } from "@/components/ui/dynamic-zone";
import { ErrorMessageString } from "@/components/ui/error-message";
import { SelectCenterButton } from "@/components/ui/select-center-button";
import { Text } from "@/components/ui/text";
import type { BlockUnionFragment, CenterOverviewFragment } from "@/generated/client.generated";
import { useCentersSortedByDistance } from "@/hooks/use-centers-sorted-by-distance";
import { paths } from "@/utils/paths-utils";
import type { SyntheticEvent } from "react";

interface CentersProps {
  centers: ReadonlyArray<CenterOverviewFragment>;
  blocks: ReadonlyArray<BlockUnionFragment>;
}

export const Centers = ({ centers: centersFromPage, blocks }: CentersProps) => {
  const {
    askedForLocationLoading,
    centers: askedForLocationCenters,
    setAskedForLocation,
    errorMessage,
  } = useCentersSortedByDistance({});
  const centers = askedForLocationCenters && !askedForLocationLoading ? askedForLocationCenters : centersFromPage;
  return (
    <ContentContainer width="large" horizontalPlacement="center">
      <FullWidthContainer backgroundColor={"#004a69"} spanTopMargin={true}>
        <VerticalContainer horizontalPlacement="center" width="full">
          <Heading color="light" type="h1">
            Finn treningssenter
          </Heading>

          <HorizontalContainer noWrap={true}>
            <VerticalSpacer size="medium" />

            <ContentContainer width="medium">
              <Text
                align="center"
                color="light"
                value="Velkommen på trening! 3T er der DU er. Vi har 16 senter i Trøndelag som gir deg alt du trenger i en variert og aktiv hverdag. Gruppetimer, store og flotte lokaler."
              />
            </ContentContainer>

            <VerticalSpacer size="medium" />
          </HorizontalContainer>
        </VerticalContainer>
        <HorizontalSpacer size="x-small" />
      </FullWidthContainer>
      <HorizontalSpacer size="large" />

      <DynamicZone blocks={blocks} />

      <LocationButton
        loading={askedForLocationLoading && !errorMessage}
        onClick={(e: SyntheticEvent) => {
          e.preventDefault();
          setAskedForLocation(true);
        }}
      />
      {errorMessage && <ErrorMessageString errorString={errorMessage} />}
      <HorizontalSpacer size="large" />

      {centers.map((center) => (
        <SelectCenterButton
          address={
            center.contactInfo?.address
              ? `${center.contactInfo?.address}, ${center.contactInfo?.zipCode} ${center.contactInfo?.city}`
              : undefined
          }
          buttonText={"Les om senteret"}
          facilities={center.facilities}
          href={paths.senter(center.slug)}
          key={center.id}
          title={center.page.title}
          imageProps={{
            alt: center.page.thumbnailImage?.alternativeText ?? "Bilde av 3T-senter",
            src: center.page.thumbnailImage?.url ?? "/images/fallback-desktop.jpg",
            width: center.page.thumbnailImage?.width ?? 2000,
            height: center.page.thumbnailImage?.height ?? 800,
            placeholder: center.page.thumbnailImage?.placeholder ?? undefined,
            priority: false,
          }}
        />
      ))}
    </ContentContainer>
  );
};
